import { Input, InputNumber } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import { CIMSRangeGroupProps } from './types'
import { useDisabledOptions } from '../../hooks/useDisabledOptions'

function isEmpty(value: null | undefined | number) {
  return value === null || value === undefined
}

/**
 * Renders a pair of input fields for specifying a minimum and maximum value.
 * The maximum value must be greater than the minimum value.
 *
 * @example
 * <CIMSRangeGroup
 *   name="slump"
 *   label="Slump Range"
 *   help={'helpful message'}
 *   required
 * />
 */
function CIMSRangeGroup({
  name,
  required,
  requireBoth,
  disabled,
  ...rest
}: CIMSRangeGroupProps) {
  const [disabledOptions, { some: someDisabled }] = useDisabledOptions(disabled)
  return (
    <CIMSFormItem
      htmlFor={`${name}_min`}
      required={required}
      disabled={disabledOptions}
      {...rest}
    >
      <Input.Group compact>
        <CIMSFormItem
          noStyle
          name={[name, 'min']}
          requiredMessage={
            required ? `Must enter min ${name} range` : undefined
          }
        >
          <InputNumber
            min={0}
            style={{ width: '50%' }}
            placeholder="min"
            disabled={someDisabled}
          />
        </CIMSFormItem>
        <CIMSFormItem
          noStyle
          name={[name, 'max']}
          dependencies={[[name, 'min']]}
          requiredMessage={
            required ? `Must enter max ${name} range` : undefined
          }
          customValidator={[
            {
              validator: (value, { getFieldValue }) => {
                const field = getFieldValue(name)
                if (isEmpty(value) || isEmpty(field.min) || value > field.min) {
                  return true
                }

                return false
              },
              message: 'Max must be greater than min',
            },
            {
              validator: (value, { getFieldValue }) => {
                if (!requireBoth) return true
                const field = getFieldValue(name)
                if (isEmpty(value) && !isEmpty(field.min)) return false
                if (!isEmpty(value) && isEmpty(field.min)) return false
                return true
              },
              message: 'Must give both a min and max value',
            },
          ]}
        >
          <InputNumber
            min={0}
            style={{ width: '50%' }}
            placeholder="max"
            disabled={someDisabled}
          />
        </CIMSFormItem>
      </Input.Group>
    </CIMSFormItem>
  )
}

export default CIMSRangeGroup
